import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import VoiceActions, { VoiceSelectors } from "../Redux/VoiceRedux";
import { DEFAULT_VOICES, DEFAULT_VOICE_ID } from "../services/elevenlabs";

export default function VoiceSelector({ 
  value, 
  onChange,
  disabled = false,
  inline = false 
}) {
  const dispatch = useDispatch();
  
  // Redux state
  const voices = useSelector(VoiceSelectors.getVoices);
  const isLoading = useSelector(VoiceSelectors.isFetching);
  const error = useSelector(VoiceSelectors.getError);
  
  // Local UI state
  const [isPlaying, setIsPlaying] = useState(null);
  const [isAudioLoading, setIsAudioLoading] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const audioRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Load voices on mount using Redux action
    dispatch(VoiceActions.voicesListRequest());
  }, [dispatch]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleVoiceSelect = (voice) => {
    onChange({
      voice_id: voice.voice_id,
      voice_name: voice.name,
    });
    setIsOpen(false);
  };

  const playPreview = (e, voice) => {
    e.stopPropagation();
    
    if (!voice.preview_url) {
      return;
    }

    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.onended = () => setIsPlaying(null);
      audioRef.current.onerror = () => {
        setIsPlaying(null);
        setIsAudioLoading(null);
      };
      audioRef.current.oncanplaythrough = () => {
        setIsAudioLoading(null);
      };
    }

    if (isPlaying === voice.voice_id) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(null);
    } else {
      setIsAudioLoading(voice.voice_id);
      audioRef.current.src = voice.preview_url;
      audioRef.current.play()
        .then(() => {
          setIsPlaying(voice.voice_id);
          setIsAudioLoading(null);
        })
        .catch((err) => {
          console.error("Audio playback failed:", err);
          setIsPlaying(null);
          setIsAudioLoading(null);
        });
    }
  };

  // Use voices from Redux, fallback to DEFAULT_VOICES if empty
  const voicesList = voices && voices.length > 0 ? voices : DEFAULT_VOICES;
  
  const selectedVoice = voicesList.find(v => v.voice_id === value) || 
    DEFAULT_VOICES.find(v => v.voice_id === value) ||
    DEFAULT_VOICES.find(v => v.voice_id === DEFAULT_VOICE_ID);

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-200 rounded-xl"></div>
      </div>
    );
  }

  // Build a short label for inline display: "Name - Gender Voice" or just name
  const inlineVoiceLabel = selectedVoice
    ? `${selectedVoice.name}${selectedVoice.gender ? ` - ${selectedVoice.gender.charAt(0).toUpperCase() + selectedVoice.gender.slice(1)} Voice` : ""}`
    : "Select Voice";

  // ── Inline layout (voice selector + preview side by side) ──
  if (inline) {
    return (
      <div className="relative" ref={dropdownRef}>
        <div className="flex items-center gap-3">
          {/* Voice trigger */}
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className={`flex-1 min-w-0 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-left flex items-center justify-between transition-all ${
              disabled ? "opacity-50 cursor-not-allowed" : "hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            } ${isOpen ? "ring-2 ring-primary-500 border-transparent" : ""}`}
          >
            <span className="font-medium text-gray-900 truncate block min-w-0" title={inlineVoiceLabel}>
              {inlineVoiceLabel}
            </span>
            <svg 
              className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ml-2 ${isOpen ? "rotate-180" : ""}`} 
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Inline preview button */}
          {selectedVoice?.preview_url && (
            <button
              type="button"
              onClick={(e) => playPreview(e, selectedVoice)}
              disabled={disabled}
              aria-label={isPlaying === selectedVoice.voice_id ? `Pause ${selectedVoice.name} preview` : `Preview ${selectedVoice.name} voice`}
              className={`flex-shrink-0 px-5 py-3.5 border rounded-xl flex items-center gap-2 transition-colors text-sm font-medium ${
                isPlaying === selectedVoice.voice_id
                  ? "bg-primary-50 border-primary-200 text-primary-700"
                  : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
              } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {isAudioLoading === selectedVoice.voice_id ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : isPlaying === selectedVoice.voice_id ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
              Preview
            </button>
          )}
        </div>

        {/* Dropdown - opens upward to avoid going off-screen */}
        {isOpen && (
          <div className="absolute z-50 w-full bottom-full mb-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-[60vh] sm:max-h-80 overflow-y-auto">
            {error && (
              <div className="px-4 py-2 text-sm text-amber-600 bg-amber-50 border-b border-amber-100">
                Using default voices (API unavailable)
              </div>
            )}
            {voicesList.map((voice) => (
              <div
                key={voice.voice_id}
                onClick={() => handleVoiceSelect(voice)}
                className={`px-4 py-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-b-0 ${
                  value === voice.voice_id ? "bg-primary-50" : ""
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{voice.name}</span>
                    {value === voice.voice_id && (
                      <span className="text-primary-500">✓</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 flex items-center gap-2 mt-0.5">
                    {voice.gender && <span className="capitalize">{voice.gender}</span>}
                    {voice.accent && <><span>•</span><span className="capitalize">{voice.accent}</span></>}
                    {voice.category && <><span>•</span><span className="capitalize">{voice.category}</span></>}
                  </div>
                  {voice.description && (
                    <div className="text-sm text-gray-400 mt-0.5 italic truncate">&quot;{voice.description}&quot;</div>
                  )}
                </div>
                {voice.preview_url && (
                  <button
                    type="button"
                    onClick={(e) => playPreview(e, voice)}
                    className={`ml-3 p-2 rounded-full transition-colors flex-shrink-0 ${
                      isPlaying === voice.voice_id ? "bg-primary-100 text-primary-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    title={isPlaying === voice.voice_id ? "Pause" : "Play preview"}
                  >
                    {isAudioLoading === voice.voice_id ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : isPlaying === voice.voice_id ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/></svg>
                    ) : (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Default layout (original) ──
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-left flex items-center justify-between transition-all ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        } ${isOpen ? "ring-2 ring-primary-500 border-transparent" : ""}`}
      >
        <div className="flex items-center gap-3">
          <span className="text-lg" aria-hidden="true">🎙️</span>
          <div>
            <span className="font-medium text-gray-900">{selectedVoice?.name || "Select Voice"}</span>
            {selectedVoice?.description && (
              <span className="text-gray-500 text-sm ml-2">- {selectedVoice.description}</span>
            )}
          </div>
        </div>
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-[60vh] sm:max-h-80 overflow-y-auto">
          {error && (
            <div className="px-4 py-2 text-sm text-amber-600 bg-amber-50 border-b border-amber-100">
              Using default voices (API unavailable)
            </div>
          )}
          {voicesList.map((voice) => (
            <div
              key={voice.voice_id}
              onClick={() => handleVoiceSelect(voice)}
              className={`px-4 py-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-b-0 ${
                value === voice.voice_id ? "bg-primary-50" : ""
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900">{voice.name}</span>
                  {value === voice.voice_id && (
                    <span className="text-primary-500">✓</span>
                  )}
                </div>
                <div className="text-sm text-gray-500 flex items-center gap-2 mt-0.5">
                  {voice.gender && (
                    <span className="capitalize">{voice.gender}</span>
                  )}
                  {voice.accent && (
                    <>
                      <span>•</span>
                      <span className="capitalize">{voice.accent}</span>
                    </>
                  )}
                  {voice.category && (
                    <>
                      <span>•</span>
                      <span className="capitalize">{voice.category}</span>
                    </>
                  )}
                </div>
                {voice.description && (
                  <div className="text-sm text-gray-400 mt-0.5 italic">
                    &quot;{voice.description}&quot;
                  </div>
                )}
              </div>
              
              {voice.preview_url && (
                <button
                  type="button"
                  onClick={(e) => playPreview(e, voice)}
                  className={`ml-3 p-2 rounded-full transition-colors flex-shrink-0 ${
                    isPlaying === voice.voice_id 
                      ? "bg-primary-100 text-primary-600" 
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  title={isPlaying === voice.voice_id ? "Pause preview" : "Play preview"}
                >
                  {isAudioLoading === voice.voice_id ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : isPlaying === voice.voice_id ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedVoice?.preview_url && !isOpen && (
        <button
          type="button"
          onClick={(e) => playPreview(e, selectedVoice)}
          disabled={disabled}
          className={`mt-2 w-full px-4 py-2 border rounded-lg flex items-center justify-center gap-2 transition-colors ${
            isPlaying === selectedVoice.voice_id
              ? "bg-primary-50 border-primary-200 text-primary-700"
              : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isAudioLoading === selectedVoice.voice_id ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading...</span>
            </>
          ) : isPlaying === selectedVoice.voice_id ? (
            <>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
              <span>Pause Preview</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
              <span>Preview Voice</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}